import {useAuth} from "@/auth";
import React, { useEffect, useState } from "react";
import { api, fmtINR, daysSince, waLink } from "@/lib/api";
import { Link } from "react-router-dom";
import { FileDown, FileText, Pencil, Download, ShieldCheck, Share2, Plus } from "lucide-react";
import { toast } from "sonner";

import { getAuthToken } from "@/lib/authToken";
export default function QuotationsPage() {
  const {user}=useAuth(); // Staff quotations auth crash fix

  const [list, setList] = useState([]);
  const [threshold, setThreshold] = useState(7);
  const [quoteSearch, setQuoteSearch] = useState("");
  const [assistantSalespeople,setAssistantSalespeople]=useState([]);
  const [assistantOwnerChoice,setAssistantOwnerChoice]=useState({});
  useEffect(()=>{
    if(user?.role==="assistant"){
      api.get("/assistant/salespeople").then(r=>setAssistantSalespeople(r.data||[]))
       .catch(()=>toast.error("Could not load responsible salespeople"));
    }
  },[user?.role]);
  const assignAssistantOwner=async(q)=>{
    const ownerId=assistantOwnerChoice[q.id]||q.responsible_salesperson_id||"";
    if(!ownerId)return toast.error("Select the responsible salesperson");
    try{
      await api.post("/assistant/quotations/"+q.id+"/owner",{responsible_salesperson_id:ownerId});
      toast.success("Responsible salesperson saved");
      load();
    }catch(e){toast.error(e.response?.data?.detail||"Could not assign owner");}
  };
  // assistant-quotation-owner-20260922

  const [selectedQuoteId,setSelectedQuoteId]=useState(null);

  const load = () => api.get(["sales","assistant"].includes(user?.role)?"/records/all/quotations":"/quotations").then(r => setList(r.data));
  useEffect(() => {
    load();
    api.get("/settings").then(r => setThreshold(r.data.quotation_overdue_days || 7)).catch(() => {});
  }, []);

  const dl = (path) => {
    const t = getAuthToken();
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api${path}?auth=${t}`, "_blank");
  };

  const fmtDate = (iso) => {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };

  const share = (q) => {
    const lines = q.items.map(i => `• ${i.product_name} × ${i.qty} @ ${fmtINR(i.unit_price)}`).join("\n");
    const text = `*Quotation for ${q.customer_name}*\n${lines}\n\nSubtotal: ${fmtINR(q.subtotal)}\nGST: ${fmtINR(q.gst_amount)}\n*Total: ${fmtINR(q.total)}*\n\nTerms: ${q.terms}\nValidity: ${q.validity_days} days`;
    window.open(waLink(text), "_blank");
  };

  const archiveQuote=async q=>{if(!window.confirm(`Move quotation ${q.quote_number||q.customer_name} to Archive?`))return;try{await api.post(`/archive/quotation/${q.id}`,{reason:""});toast.success("Quotation moved to Archive");load()}catch(e){toast.error(e.response?.data?.detail||"Archive failed")}};
  const filteredList=list.filter(q=>{
    const term=quoteSearch.trim().toLowerCase();
    return !term||[
      q.quote_number,q.serial_no,q.doc_code,q.customer_name,q.customer_phone,
      q.quote_date,q.created_at,q.created_by_name,
      ...(q.items||[]).flatMap(i=>[i.product_name,i.vehicle_name,i.description,i.addon_type])
    ].filter(x=>x!=null).join(" ").toLowerCase().includes(term);
  });

  const smallBtn = "h-9 px-3.5 rounded-lg border border-slate-300 bg-white text-xs font-medium flex items-center gap-1.5 hover:bg-slate-50 transition-colors";

  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">My Quotes</h1>
          <p className="text-sm text-slate-500 mt-1">Quotations you created.</p>
        </div>
        <Link to="/quotes/drafts" className="inline-flex h-10 items-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700">View Drafts</Link>
        <Link to="/quotation" data-testid="quotations-new-button"
          className="h-9 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium flex items-center gap-1.5 shrink-0 mt-1 transition-colors">
          <Plus className="w-3.5 h-3.5" /> New
        </Link>
      </div>

      <button onClick={() => dl("/exports/quotations")} data-testid="quotes-export-excel"
        className="mt-4 w-full h-11 rounded-xl border border-slate-300 bg-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors">
        <Download className="w-4 h-4" /> Download list as Excel
      </button>

      <div className="mt-4"><input value={quoteSearch} onChange={e=>setQuoteSearch(e.target.value)} placeholder="Search customer, quote number, serial, vehicle or date…" className="w-full h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm" /></div>

      <div className="space-y-3 mt-4">
        {filteredList.map(q => {
          const age = daysSince(q.created_at);
          const overdue = q.status === "draft" && age > threshold;
          return (
            <div key={q.id} className={"bg-white border rounded-2xl p-4 " + (selectedQuoteId===q.id?"border-blue-500 ring-1 ring-blue-200":"border-slate-200")} data-testid={`quote-row-${q.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{q.customer_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono-tab">{q.quote_number || q.id.slice(0, 8).toUpperCase()} · {fmtDate(q.created_at)}</div>
                  <div className="text-xs text-slate-600 mt-1 truncate">{q.items[0]?.product_name}{q.items.length > 1 ? ` +${q.items.length - 1} more` : ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold text-blue-600 tabular-nums">{fmtINR(q.total)}</div>
                  <div className={`text-[11px] font-semibold mt-0.5 ${q.status === "confirmed" ? "text-emerald-600" : "text-blue-600"}`}>
                    {q.status === "confirmed" ? "Confirmed (PC)" : "Quotation"}
                  </div>
                </div>
              </div>
              {selectedQuoteId===q.id && <div className="mt-3 text-xs text-blue-700 bg-blue-50 rounded-lg p-3">
                 {q.status==="draft"?"Selected · use Edit below to change this quotation. Its number is retained.":"Selected · confirmed quotation is locked; edit the related PC from All PCs."}
               </div>}
              {overdue && (
                <div className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-2.5" data-testid={`quote-overdue-${q.id}`}>
                  Overdue · {age} days old — follow up
                </div>
              )}
              {user?.role==="assistant"&&q.created_by===user?.id&&q.status!=="confirmed"&&<div className="mt-3 flex flex-col gap-2 rounded-xl border border-blue-200 p-3">
              <label className="text-xs font-semibold text-slate-600">Responsible Salesperson *</label>
              <select aria-label="Responsible Salesperson for quotation" value={assistantOwnerChoice[q.id]??q.responsible_salesperson_id??""}
               onChange={e=>setAssistantOwnerChoice(prev=>({...prev,[q.id]:e.target.value}))}
               className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm">
               <option value="">Select sales owner</option>
               {assistantSalespeople.map(o=><option value={o.id} key={o.id}>{o.name}{o.pc_prefix?" ("+o.pc_prefix+")":""}</option>)}
              </select>
              <button type="button" onClick={()=>assignAssistantOwner(q)} className="h-9 rounded-lg bg-blue-600 px-3 text-sm text-white">Save Responsible Salesperson</button>
            </div>}
            <div className="flex gap-2 mt-3 flex-wrap">
                <button type="button" onClick={()=>setSelectedQuoteId(selectedQuoteId===q.id?null:q.id)} className={smallBtn}>{selectedQuoteId===q.id?"Selected":"Select"}</button>
                <button onClick={() => dl(`/quotations/${q.id}/pdf`)} data-testid={`quote-pdf-${q.id}`} className={smallBtn}><FileDown className="w-3.5 h-3.5" /> PDF</button>
                <button onClick={() => dl(`/quotations/${q.id}/word`)} data-testid={`quote-word-${q.id}`} className={smallBtn}><FileText className="w-3.5 h-3.5" /> Word</button>
                <button onClick={() => share(q)} data-testid={`quote-share-${q.id}`} className={smallBtn}><Share2 className="w-3.5 h-3.5" /> Share</button>
                {q.status === "draft" && (
                  <>
                    <Link to={`/quotation?edit=${q.id}`} data-testid={`quote-edit-${q.id}`} className={smallBtn}><Pencil className="w-3.5 h-3.5" /> Edit</Link>
                    <Link to={`/pcs/new/${q.id}`} data-testid={`quote-confirm-${q.id}`}
                      className="h-9 px-3.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium flex items-center gap-1.5 transition-colors">
                      <ShieldCheck className="w-3.5 h-3.5" /> Create PC
                    </Link>
                  </>
                )}
                {(["admin","assistant"].includes(user?.role)||q.created_by===user?.id)&&<Link to={"/quotes/edit/"+q.id} className="h-9 px-3.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50">Edit</Link>}<button onClick={()=>archiveQuote(q)} className="h-9 px-3.5 rounded-lg border border-rose-200 bg-white text-rose-600 text-xs font-medium hover:bg-rose-50">Delete</button>
              </div>
            </div>
          );
        })}
        {filteredList.length === 0 && <div className="text-center py-10 text-slate-500 text-sm">No quotations yet. Create one from the Quotation card on Home.</div>}
      </div>
    </div>
  );
}
